<template>
  <form-layout
    :title="formattedTitle"
    :is-saving="creatingGuestUser"
    save-label="Save"
    discard-label="Cancel"
    has-changes
    @on-dismiss="discard"
    @on-save="save"
    @on-discard="dismiss"
  >
    <vs-form ref="form">
      <vs-wrapper>
        <vs-text-input label="Name" :value="formData.name" required @input="setName" />
        <vs-text-input label="Email" :value="formData.email" required @input="setEmail" />
      </vs-wrapper>
    </vs-form>
    <template #snack-bar-wrapper>
      <vs-snackbar
        v-model="isSnackbarVisible"
        type="error"
        sticky
        data-test="line-items-form-snackbar"
      >
        {{ snackbarErrorMessage }}
      </vs-snackbar>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import FormLayout from '@/components/common/FormLayout.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { computed, onMounted, reactive, ref } from 'vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import { useCreateUserMutation } from '@/api/users'
import { useHubs } from '@/common/useHubs'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { useStore } from 'vuex-composition-helpers'
import { AxiosError } from 'axios'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'
import validateEmail from '@/utils/email-validation'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'

const props = defineProps<{
  title?: string
  initialNameValue?: string
}>()
interface FormData {
  email?: string | null
  name?: string | null
}
const emit = defineEmits<{
  (e: 'dismiss'): void
  (e: 'user-created', userSid: string): void
  (e: 'user-email-created', userEmail: string): void
}>()
const store = useStore()
const formData = reactive<FormData>({
  email: undefined,
  name: undefined,
})
onMounted(() => {
  if (props.initialNameValue) {
    formData.name = props.initialNameValue
  }
})

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const isSnackbarVisible = ref(false)
const snackbarErrorMessage = ref()
const form = ref()
const { currentHub } = useHubs()
const { canCreateNewGuestRequester } = useBusinessUnitPermissions()

const {
  mutateAsync: createGuestUser,
  isLoading: creatingGuestUser,
  error: createGuestUserError,
  isError: isCreateGuestUserError,
} = useCreateUserMutation((userSid: string) => {
  store.dispatch('users/fetchUsersBySid', userSid)
})

const formattedTitle = computed(
  () => props.title ?? `Create new ${currentHub.value?.name} requester`,
)
const setEmail = (newEmail: string) => {
  formData.email = newEmail
}
const setName = (newName: string) => {
  formData.name = newName
}
async function save() {
  if (!form.value.isValid() || !canCreateNewGuestRequester.value) return
  if (!formData.email || !validateEmail(formData.email)) {
    snackbarErrorMessage.value = 'Invalid email'
    isSnackbarVisible.value = true
    return
  }
  try {
    const response = await createGuestUser({
      defaultTeamId: null,
      name: formData.name!,
      email: formData.email!,
      phone: null,
      roles: ['Customer', 'Super Requester'],
      scopes: [currentHub.value?.id ?? ''],
      sendEmailToNewUser: false,
    })
    if (!isCreateGuestUserError.value) {
      emit('user-created', response)
      emit('user-email-created', formData.email!)
      dismiss()
    }
  } catch (e) {
    if (
      createGuestUserError.value &&
      (createGuestUserError.value as AxiosError).response?.status === 409
    ) {
      snackbarErrorMessage.value = 'A user with this email already exists'
    } else {
      snackbarErrorMessage.value = createGuestUserError.value
    }
    isSnackbarVisible.value = true
  }
}

function dismiss() {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isNewGuestUserBottomSheetVisible: false,
  })
  emit('dismiss')
}

function discard() {
  let confirmed = true

  if (!!formData.email || !!formData.name) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?',
    )

    if (!confirmation) {
      confirmed = false
    }
  }

  if (confirmed) {
    dismiss()
  }
}
</script>
